import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useAuth } from "../auth";
import { TripCtx } from "./TripLayout";
import { toast } from "sonner";
import { Copy, Upload } from "lucide-react";

const currencySymbol = (c) => ({ EUR: "€", GBP: "£" }[c] || c);

export default function BudgetPage() {
  const { tripId } = useParams();
  const { API, authHeaders, token } = useAuth();
  const { trip } = useContext(TripCtx);
  const [lines, setLines] = useState([]);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => axios.get(`${API}/trips/${tripId}/budget`, { headers: authHeaders }).then((r) => setLines(r.data));
  useEffect(() => { load(); }, [tripId]);

  const startCheckout = async (line) => {
    setBusy(line.id);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`,
        { budget_line_id: line.id, origin_url: window.location.origin },
        { headers: authHeaders });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error("Checkout failed"); setBusy(null); }
  };

  const uploadReceipt = async (line, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(line.id);
    try {
      await axios.post(`${API}/budget/${line.id}/receipt`, fd,
        { headers: { ...authHeaders, "Content-Type": "multipart/form-data" } });
      toast.success("Receipt uploaded — our team is reviewing");
      load();
    } catch (err) { toast.error("Upload failed"); }
    finally { setBusy(null); setUploadingFor(null); }
  };

  const total = lines.reduce((s, l) => s + l.amount, 0);
  const paid = lines.filter((l) => l.status === "paid").reduce((s, l) => s + l.amount, 0);

  return (
    <div data-testid="budget-page" className="py-4">
      <p className="tcm-eyebrow">Payments · {trip.currency}</p>
      <h1 className="font-display text-4xl mt-2">Budget</h1>
      <hr className="tcm-gold-rule w-16 my-6" />
      <div className="grid grid-cols-2 gap-4 mb-8">
        <div className="border border-[#1E2D50] p-4">
          <p className="tcm-eyebrow">Total</p>
          <p className="font-display text-3xl mt-1">{currencySymbol(trip.currency)}{total.toFixed(0)}</p>
        </div>
        <div className="border border-[#1E2D50] p-4">
          <p className="tcm-eyebrow">Paid</p>
          <p className="font-display text-3xl mt-1">{currencySymbol(trip.currency)}{paid.toFixed(0)}</p>
        </div>
      </div>

      <div className="space-y-6">
        {lines.length === 0 && <p className="text-sm text-[color:var(--tcm-navy-70)] py-6">No budget lines yet.</p>}
        {lines.map((l) => {
          const chipClass = l.status === "paid" ? "tcm-chip-paid" : l.status === "checking" ? "tcm-chip-checking" : "tcm-chip-due";
          return (
            <div key={l.id} className="tcm-double-frame" data-testid={`budget-line-${l.id}`}>
              <div className="tcm-inner p-6">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1">
                    <p className="tcm-eyebrow">{l.type === "tcm_fee" ? "The Curated Mile" : "Supplier · " + (l.supplier_name || "")}</p>
                    <p className="font-display text-2xl mt-1">{l.label}</p>
                    <p className="mt-2 font-display text-3xl">{currencySymbol(l.currency)}{l.amount.toFixed(2)}</p>
                  </div>
                  <span className={`tcm-chip ${chipClass}`} data-testid={`status-${l.id}`}>{l.status}</span>
                </div>

                {l.type === "supplier" && l.bank_details && l.status !== "paid" && (
                  <div className="mt-6 border-t border-[color:var(--tcm-navy-15)] pt-4">
                    <p className="tcm-eyebrow mb-2">Bank details</p>
                    <pre className="whitespace-pre-wrap text-sm font-body">{l.bank_details}</pre>
                    <button data-testid={`copy-${l.id}`} onClick={() => { navigator.clipboard.writeText(l.bank_details); toast.success("Copied"); }}
                            className="mt-3 tcm-btn-outline inline-flex items-center gap-2">
                      <Copy size={12} /> Copy
                    </button>
                    {l.supplier_link && (
                      <a href={l.supplier_link} target="_blank" rel="noreferrer" className="ml-2 tcm-btn-outline inline-block">Open supplier</a>
                    )}
                  </div>
                )}

                {l.status === "due" && l.type === "tcm_fee" && (
                  <button data-testid={`pay-${l.id}`} disabled={busy === l.id} onClick={() => startCheckout(l)} className="mt-5 tcm-btn-primary w-full">
                    {busy === l.id ? "Redirecting..." : "Pay with card"}
                  </button>
                )}

                {l.status === "due" && l.type === "supplier" && (
                  <>
                    {uploadingFor === l.id ? (
                      <div className="mt-5 space-y-3">
                        <input data-testid={`receipt-file-${l.id}`} type="file" accept="image/*,application/pdf"
                               onChange={(e) => uploadReceipt(l, e.target.files[0])}
                               className="tcm-input" />
                        <button onClick={() => setUploadingFor(null)} className="tcm-btn-outline w-full">Cancel</button>
                      </div>
                    ) : (
                      <button data-testid={`ive-paid-${l.id}`} onClick={() => setUploadingFor(l.id)} className="mt-5 tcm-btn-primary w-full inline-flex items-center justify-center gap-2">
                        <Upload size={14} /> I've paid — upload receipt
                      </button>
                    )}
                  </>
                )}

                {l.status === "checking" && (
                  <div className="mt-5 border-t border-[color:var(--tcm-navy-15)] pt-4">
                    <p className="tcm-eyebrow">Awaiting confirmation</p>
                    <p className="text-sm mt-1 text-[color:var(--tcm-navy-70)]">Your receipt is with our team.</p>
                    {l.receipt && (
                      <a href={`${API}/receipts/${l.receipt.id}/download?auth=${token}`} target="_blank" rel="noreferrer"
                         className="tcm-eyebrow underline decoration-[#C9A961] underline-offset-4 mt-2 inline-block">View receipt</a>
                    )}
                  </div>
                )}

                {l.status === "paid" && (
                  <div className="mt-5 border-t border-[color:var(--tcm-navy-15)] pt-4 flex flex-wrap items-center gap-3">
                    <p className="tcm-eyebrow flex-1">Payment confirmed</p>
                    <a data-testid={`download-receipt-${l.id}`}
                       href={`${API}/budget/${l.id}/payment-receipt.pdf?auth=${token}`}
                       target="_blank" rel="noreferrer"
                       className="tcm-btn-outline text-[11px] py-2 px-3 inline-flex items-center gap-2">
                      Download receipt
                    </a>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
